import { Mutex } from 'async-mutex';
// @ts-ignore
import type * as RCL from 'rclnodejs';

import {
  type CartesianPose,
  rosTool0ToRosTooltip,
  baseCoordinatesToTooltip,
} from '@sb/geometry';
import { info, namespace } from '@sb/log';
import type { ArmJointPositions, FrameOfReference } from '@sb/motion-planning';
import { getRCL } from '@sb/ros/getRCL';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile/SpeedProfile';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import { BaseAction } from './BaseAction';
import { JOINT_NAMES } from './constants';
import { PlanningGoalNotSucceededError } from './errors/PlanningGoalNotSucceededError';

const ns = namespace('JogAction');

export type JoggingActionRequest = {
  startJointState: number[];
  speedProfile: SpeedProfile;
  manualRecoveryMode: boolean;
  useScalingForSpeedLimits?: boolean;
  payloadMass?: number;
  tcpLink?: string;
  tcpOrientationFromTool0?: {
    i: number;
    j: number;
    k: number;
    w: number;
  };
} & (
  | {
      jogType: 'joint';
      displacements: ArmJointPositions;
    }
  | {
      jogType: 'cartesian';
      pose: CartesianPose;
      frame: FrameOfReference;
    }
);

export class JoggingAction extends BaseAction<
  'standard_bots_msgs/action/Jogging',
  RCL.standard_bots_msgs.action.Jogging_Goal,
  RCL.standard_bots_msgs.action.Jogging_Result
> {
  private static joggingMutex = new Mutex();

  private request: JoggingActionRequest;

  public constructor(request: JoggingActionRequest) {
    super();
    this.request = request;
  }

  protected override getType(): RCL.TypeClass<keyof RCL.ActionsMap> {
    return 'standard_bots_msgs/action/Jogging';
  }

  protected override getTopic() {
    return '/jog';
  }

  protected override getMutex(): Mutex {
    return JoggingAction.joggingMutex;
  }

  protected override async createGoal() {
    const rcl = await getRCL();

    const joggingAction = rcl.require('standard_bots_msgs/action/Jogging');

    const goalMessage = new joggingAction.Goal();

    info(ns`createGoal`, 'creating jogging goal message');

    // Set joint jog command
    if (this.request.jogType === 'joint') {
      goalMessage.joint_jog_cmd = this.createJointJogCmd(rcl);
      goalMessage.jog_cmd = joggingAction.Goal.JOINT_CMD;
    } else {
      // this.request.jogType === 'cartesian'
      goalMessage.cartesian_jog_cmd = this.createCartesianJogCmd(rcl);
      goalMessage.jog_cmd = joggingAction.Goal.CARTESIAN_CMD;
    }

    // Set start joint state
    goalMessage.start_joint_state = this.getStartJointState(rcl);

    info(
      ns`createGoal`,
      `finished creating jogging goal message: ${JSON.stringify(goalMessage.toPlainObject())}`,
    );

    return goalMessage;
  }

  private createPlanOptions(rcl: typeof RCL) {
    const planOptions = rcl.createMessageObject(
      'standard_bots_msgs/msg/PlanOptions',
    );

    info(ns`createPlanOptions`, 'speedprofile', this.request.speedProfile);

    planOptions.velocity_scaling =
      this.request.speedProfile.baseVelocityScaling;

    planOptions.acceleration_scaling =
      this.request.speedProfile.baseAccelerationScaling;

    planOptions.max_acceleration_limit = [
      ...this.request.speedProfile.maxJointAccelerations,
    ];

    planOptions.max_velocity_limit = [
      ...this.request.speedProfile.maxJointSpeeds,
    ];

    planOptions.max_tooltip_velocity =
      this.request.speedProfile.maxTooltipSpeed;

    planOptions.use_torque_limits = getFeatureFlagSync(
      'torqueAwareMotions',
      false,
    );

    planOptions.joint_torque_limits = [
      ...this.request.speedProfile.maxJointTorques,
    ];

    planOptions.payload_mass = this.request.payloadMass ?? 0;

    planOptions.tcp_link = this.request.tcpLink ?? 'tool0';

    if (this.request.useScalingForSpeedLimits) {
      planOptions.use_scaling_for_speed_limits = true;
    } else {
      planOptions.use_scaling_for_speed_limits = false;
    }

    info(ns`createPlanOptions`, 'planOptions', planOptions);

    return planOptions;
  }

  private getStartJointState(rcl: typeof RCL) {
    const jointState = rcl.createMessageObject('sensor_msgs/msg/JointState');

    jointState.name = JOINT_NAMES;

    info(
      ns`getStartJointState`,
      `setting start positions to ${this.request.startJointState}`,
    );

    jointState.position = this.request.startJointState;

    return jointState;
  }

  private createJointJogCmd(rcl: typeof RCL) {
    if (this.request.jogType !== 'joint') {
      throw new Error('Joint jog command not provided');
    }

    const jointJogWithOptions = rcl.createMessageObject(
      'standard_bots_msgs/msg/JointJogWithOptions',
    );

    const now = new rcl.Clock().now();
    // Create and set JointJog message
    const jointJog = rcl.createMessageObject('control_msgs/msg/JointJog');
    jointJog.displacements = this.request.displacements;
    jointJog.velocities = []; // ROS2 side ignores this field
    jointJog.duration = 0; // ROS2 side ignores this field
    jointJog.joint_names = JOINT_NAMES;

    jointJog.header.stamp.sec = now.secondsAndNanoseconds.seconds;
    jointJog.header.stamp.nanosec = now.secondsAndNanoseconds.nanoseconds;
    jointJog.header.frame_id = 'world';

    jointJogWithOptions.joint_jog_cmd = jointJog;

    jointJogWithOptions.plan_options = this.createPlanOptions(rcl);

    jointJogWithOptions.check_collisions = !this.request.manualRecoveryMode;

    return jointJogWithOptions;
  }

  private createCartesianJogCmd(rcl: typeof RCL) {
    if (this.request.jogType !== 'cartesian') {
      throw new Error('Cartesian jog command not provided');
    }

    const cartesianJog = rcl.createMessageObject(
      'standard_bots_msgs/msg/CartesianJog',
    );

    // Create and set PoseStamped message
    const pose = rcl.createMessageObject('geometry_msgs/msg/PoseStamped');

    const now = new rcl.Clock().now();

    pose.header.stamp.sec = now.secondsAndNanoseconds.seconds;
    pose.header.stamp.nanosec = now.secondsAndNanoseconds.nanoseconds;

    // Direct mapping of frame IDs to the C++ Frame enum values
    pose.header.frame_id = this.request.frame;

    let ros2Pose: CartesianPose;

    if (this.request.frame === 'robotArm' || this.request.frame === 'world') {
      ros2Pose = this.request.pose;
    } else {
      // Frame: tooltip

      // Step 1) Convert pose offsets from base coordinates -> ROS flange/tool0 coordinates
      const rosTcpPoseOffset: CartesianPose = baseCoordinatesToTooltip(
        this.request.pose,
      );

      // Step 2) If targeting a specific tooltip_link with a known orientation
      // relative to tool0, compensate by rotating the position/orientation by
      // the inverse of that orientation so commands are expressed in that link's axes.
      const needsCompensation =
        this.request.tcpLink &&
        this.request.tcpLink !== 'tool0' &&
        this.request.tcpOrientationFromTool0;

      info(
        ns`createCartesianJogCmd.tooltipFlow`,
        `needsCompensation: ${JSON.stringify(needsCompensation)}`,
      );

      if (needsCompensation) {
        const tcpOrientationFromTool0 = this.request
          .tcpOrientationFromTool0 as {
          i: number;
          j: number;
          k: number;
          w: number;
        };

        ros2Pose = rosTool0ToRosTooltip(
          rosTcpPoseOffset,
          tcpOrientationFromTool0,
        );
      } else {
        ros2Pose = rosTcpPoseOffset;
      }
    }

    pose.pose.position.x = ros2Pose.x;
    pose.pose.position.y = ros2Pose.y;
    pose.pose.position.z = ros2Pose.z;

    pose.pose.orientation.x = ros2Pose.i;
    pose.pose.orientation.y = ros2Pose.j;
    pose.pose.orientation.z = ros2Pose.k;
    pose.pose.orientation.w = ros2Pose.w;

    cartesianJog.cartesian_jog_cmd = pose;

    cartesianJog.plan_options = this.createPlanOptions(rcl);

    return cartesianJog;
  }

  public async getErrorMessage(): Promise<string | undefined> {
    if (this.goalHandle) {
      const result = await this.goalHandle.getResult();

      if (!result.success) {
        return result.error;
      }
    }

    return undefined;
  }

  protected override onGoalNotSucceeded(
    _result: RCL.standard_bots_msgs.action.Jogging_Result | undefined,
  ): never {
    throw new PlanningGoalNotSucceededError('Jogging goal not succeeded');
  }
}
